// ボスの致命 (主人公が崩れた時の決め技) を、パリィで弾ける仕組み (2026-10-09 に作り・2026-10-10 配布版の既定にした)。
//
// 設計の元 = docs/research/2026-10-09-boss-execution-counter-design.md の案A。ユーザーの答え (10-09):
//   弾いた後 = 押されて仕切り直し (ボスの体幹は普段の弾きと同じだけ溜まる)。
//     → 2026-10-12 から、ボスの体幹は普段の弾き3回ぶん溜まる (崩れる1歩手前まで・下の EXEC_PARRY_POSTURE)。旧は DEV ?execParryGain=old。
//   合図 (仮) = 今ある紫の「弾ける重い技」のオーラ＋一瞬の静止／崩れ中の回避 = 効かない (仮)。
// 配布版の既定にした根拠 = 10-10 答えるページ exec-parry-ship「入れる」(致命の傷を体力の6割にしたのは「崩れた時に弾ける救済」が前提)。
//   パリィは1種類 (10-10・src/parry_one.ts) なので、受付の厳しさの分け方 (旧の 'just' / 'any') はやめた。
//   崩れ中の受付は普段のパリィと同じ数え方・同じ長さを共有する (parryOneCatchMs(playerParryActiveMs()) = 120ms・stunParryWindowAt)。
//
// 既定は 'on'。旧 (弾けない・崩れ 1800ms 固定) は DEV の ?execParry=off だけ (URL を読むのは game.ts の DEV の箱の中)。
//   'on' の時に効く4つ:
//   1. 崩れたら、ボスは連撃・連の残りを捨てる。溜めの途中の普通の技は致命へ切り替える (boss.ts)。
//      2026-10-12 から既定は今の拍も打ち切る (振りの残りと戻りを早回しして約 0.2 秒で致命の溜めへ・src/stun_cut.ts)。
//      旧 (今の拍の当たりと戻りまでは出し切る) は DEV ?stunCut=0。
//   2. 崩れの長さ = 「致命が当たるか弾かれるまで」(1800ms 固定をやめる)。崩れ中は普通の技が当たらない (player.ts / hits.ts)。
//   3. 崩れの間もパリィのボタンだけ受け付ける。致命の当たりの瞬間に受付に入っていれば弾く (player.ts / hits.ts)。
//   4. 合図 = 致命の溜めに紫のオーラ＋溜めの頭で一瞬止まる (render.ts / game.ts)。
//   致命を持たないボス (名も無き亡霊の「崩されて弾く」課題の外など) は 'off' と同じ (崩れ 1800ms・致命は来ない)。

import type { Boss, FxEvent, Player } from './types'
import { PLAYER } from './balance'
import { playerParryActiveMs } from './difficulty'
import { getBossDefinitionById } from './bosses'
import { parryOneCatchMs, parryOneOn } from './parry_one'

export type ExecParryMode = 'off' | 'on'

let mode: ExecParryMode = 'on'

/** URL の値から切り替える。'off' / '0' = 旧 (弾けない)。それ以外 (指定なし・旧の綴り 'just' / 'any' を含む) は全部 'on' (既定)。 */
export function setExecParryMode(value: string | null | undefined): ExecParryMode {
  mode = value === 'off' || value === '0' ? 'off' : 'on'
  return mode
}
export function execParryMode(): ExecParryMode { return mode }
export function execParryEnabled(): boolean { return mode !== 'off' }

export const EXEC_PARRY = {
  /** 崩れの上限 (保険)。致命が来ないまま固まらないように。通常はこれより前に致命が当たるか弾かれる。 */
  stunCapMs: 6000,
  /** 合図の一瞬の静止 (致命の溜めに入った瞬間・溜めの速さは変えない)。 */
  signalFreezeMs: 120,
  /** 弾けた瞬間の止め (普段のジャストの最大 ≒195ms と同じくらい・少し長め)。 */
  successHitStopMs: 210,
  /** 弾けた光の強さ (普段のジャストは 0.25〜1・上限 2.0)。音の強さもこれに付いて来る (main.ts)。 */
  sparkIntensity: 1.6,
  successShake: { amplitude: 10, durationMs: 300 },
  /** 押される距離の減り方 (1コマごとに残りのこの割合だけ進む)。普段のノックバックは 0.28 (鋭い)。
   *  重い攻撃を受けた手触りにするため遅めに減速させる: 80px なら 0.1 秒で半分強・0.35 秒でほぼ止まる。 */
  pushConsumePerTick: 0.17,
} as const

/** 押されて後ずさる距離 (px)。重いボスほど遠くへ。等速の動画で見て決めた仮の値。 */
const PUSH_PX: Record<string, number> = {
  'dream-hound': 96,
  'vigil-knight': 84,
  bride: 72,
  'the-executioner': 92,
  'crowned-king-awoken': 96,
}
export const EXEC_PARRY_DEFAULT_PUSH_PX = 80
export function execParryPushPx(bossId: string): number {
  return PUSH_PX[bossId] ?? EXEC_PARRY_DEFAULT_PUSH_PX
}

/** 押された距離を滑り切るまでの時間 (ms・止めの間は数えない)。player.ts の減り方 (1コマ 1/60 秒・残り 1px 未満で止まる) と同じ数え方。 */
export function execParrySlideMs(pushPx: number): number {
  let rem = Math.abs(pushPx), ticks = 0
  while (rem >= 1 && ticks < 600) { rem -= rem * EXEC_PARRY.pushConsumePerTick; ticks++ }
  return (ticks + 1) * (1000 / 60)
}
/** 滑り終わってから受けた姿を保つ時間 (歩きの判定の持ち越し 0.15 秒より少し長く)。 */
export const EXEC_PARRY_SETTLE_MS = 170

/** 稽古の課題の間だけ持たせる決め技 (配布版の稽古と DEV ?tutFocus=1|2|3 の「崩されて弾く」課題で使う・src/tut_focus.ts)。
 *  game.ts が毎コマ、いまの課題から決め直す (課題の外では null に戻る。ただし game.ts で時間を止めている間 (ポーズ・カード・ヒットストップ・playing 以外) は決め直さないので前の値が残る)。旧課題 (DEV ?tutFocus=0) では指定しない。 */
let tutorialFinisher: { bossId: string; attackId: string } | null = null
export function setTutorialFinisher(bossId: string | null, attackId: string | null | undefined): void {
  tutorialFinisher = bossId && attackId ? { bossId, attackId } : null
}

/** 崩れた主人公へ出す致命の技 id (無ければ undefined)。稽古の課題の決め技が立っていればそちらを先に見る。 */
export function bossFinisherAttackId(bossId: string): string | undefined {
  if (tutorialFinisher && tutorialFinisher.bossId === bossId) return tutorialFinisher.attackId
  return getBossDefinitionById(bossId)?.finisherAttackId
}

/** このボスは崩れた主人公へ致命を出すか (亡霊は配布版の稽古と DEV ?tutFocus=1|2|3 の「崩されて弾く」課題の間だけ出す)。 */
export function bossHasFinisher(bossId: string): boolean {
  return !!bossFinisherAttackId(bossId)
}

/** 弾ける仕組みが効いている崩れか (= 崩れは致命まで続き、パリィだけ受け付ける)。既定で真 (致命を持つボスの崩れ・亡霊の「崩されて弾く」課題を含む)・DEV ?execParry=off と致命を持たないボスの崩れは偽。 */
export function execStunActive(player: Player, bossId: string): boolean {
  return mode !== 'off' && player.state === 'stunned' && bossHasFinisher(bossId)
}

/** 崩れ中のパリィの、押してから次に受け付けるまでの間の比較版 (DEV ?stunRepress=320|200・2026-10-09 夜)。
 *  null = 既定 (受付と足し分 200＋戻り120＋空振りの硬直150 = 470ms = 普段の空振りパリィ1回と同じ)。
 *  覚醒王の台帳 (docs/ledger/awoken-king.md)「このままでいい (覚えて押す技)」= ?stunRepress は採らない (既定 470ms のまま)。
 *  320 = 空振りの硬直 150ms を抜く (受付＋戻り)／200 = パリィ1種類の前の受付 200ms の間だけ (受付が閉じたらすぐ押し直せる)。
 *  変えるのは「押し直せるまでの間」だけ。受付の長さ (stunParryCatchMs = 普段と同じ 120ms)・当たり判定の窓・姿の長さは変えない。
 *  難易度スイッチの救済 (?hatsuenTime=1・playerParryActiveMs が 240ms) の時は、押し直せる時刻だけ追従する
 *  (320→360・200→240・既定 470→510)。受付は普段のパリィと同じく 120ms のまま (救済の +40 はパリィ1種類の上限で打ち消される)。 */
export type StunRepressMs = 320 | 200 | null
let stunRepress: StunRepressMs = null
/** URL の値から切り替える。'320' / '200' 以外 (知らない値・指定なし) は全部 null (今のまま)。 */
export function setStunRepress(value: string | null | undefined): StunRepressMs {
  stunRepress = value === '320' ? 320 : value === '200' ? 200 : null
  return stunRepress
}
export function stunRepressSetting(): StunRepressMs { return stunRepress }

/** 崩れ中のパリィの1回分の姿の長さ = 普段の空振りパリィと同じ (受付120ms＋足し分80ms＋戻り120ms＋空振りの硬直150ms・player.ts の parryCycleTailMs)。
 *  描画 (姿を出し続ける長さ) だけが使う。比較版 ?stunRepress でも変えない (押し直せば姿はその時から作り直す)。 */
export function stunParryPoseMs(): number {
  return PLAYER.parryStartupMs + playerParryActiveMs() + PLAYER.parryRecoveryMs + PLAYER.wastedParryStiffnessMs
}

/** 崩れ中のパリィを押してから、次に受け付けるまでの間 (ms)。この間はもう一度押しても受け付けない (連打で窓を埋めさせない)。
 *  既定は姿の長さと同じ (470)。?stunRepress=320|200 で短くする (上の説明)。 */
export function stunParryCycleMs(): number {
  const active = PLAYER.parryStartupMs + playerParryActiveMs()
  if (stunRepress === 200) return active
  if (stunRepress === 320) return active + PLAYER.parryRecoveryMs
  return stunParryPoseMs()
}

/** 崩れ中に押したパリィを受け付けるか (前の押しの間が明けているか)。 */
export function stunParryAccepts(player: Player, nowMs: number): boolean {
  const last = player.stunParryPressMs
  // 剣舞の手拍子の致命 (src/blade_finisher_v2.ts・既定) で1撃目を受け止めた押しは、すぐ押し直せる (2撃目は1拍 520ms 後)。
  return last === undefined || player.stunParryCaughtMs === last || nowMs - last >= stunParryCycleMs()
}

/** 崩れ中のパリィの受付の長さ (ms・押してから)。普段のパリィ (player.ts enterParrying) と同じ関数で数える = 二重に数値を持たない。
 *  既定 (パリィ1種類) = 120ms (parry_one.ts PARRY_ONE_WINDOW_MS)。崩れ中は連打の縮みが掛からない (押し直せるまでの間 470ms が連打の閾値より長い) ので
 *  縮む前の受付 playerParryActiveMs() を渡す。DEV ?parryOne=off (旧) の時は 200ms (普段のパリィも同じ)。 */
export function stunParryCatchMs(): number {
  return parryOneCatchMs(playerParryActiveMs())
}

/** 崩れ中に押したパリィの、いまの受付の種類。'just' = 弾きの扱い (パリィ1種類では受付の全部)／'normal' = 旧の普通のパリィ
 *  (DEV ?parryOne=off で 80ms を過ぎた所だけ)／null = 受付の外。普段のパリィ (hits.ts の isJust・isPlayerParryActive) と同じ物差し。 */
export function stunParryWindowAt(player: Player, nowMs: number): 'just' | 'normal' | null {
  const press = player.stunParryPressMs
  if (press === undefined) return null
  const elapsed = nowMs - press
  if (elapsed < PLAYER.parryStartupMs - 1e-6 || elapsed >= PLAYER.parryStartupMs + stunParryCatchMs() - 1e-6) return null
  return parryOneOn() || elapsed <= PLAYER.perfectParryWindowMs + 1e-6 ? 'just' : 'normal'
}

/** この受付なら致命を弾けるか (受付に入っていれば弾く・旧 ?execParry=off では弾けない)。 */
export function execParryAccepts(window: 'just' | 'normal' | null): boolean {
  return window !== null && mode !== 'off'
}

/** 描画用: 崩れ中に押したパリィの姿をいま出すか。出すなら押してからの経過 (秒)。 */
export function stunParryPoseElapsedSec(player: Player, nowSec: number): number | undefined {
  if (player.state !== 'stunned' || player.stunParryPressMs === undefined) return undefined
  const elapsedMs = nowSec * 1000 - player.stunParryPressMs
  if (elapsedMs < 0 || elapsedMs >= stunParryPoseMs()) return undefined
  return elapsedMs / 1000
}

// ===================================================================================
// 致命を弾いた時のボスの体幹 (2026-10-12・配布版の既定・全ボス共通)。
//   ユーザー (検収パック 10-12 K-5 のメモ):「全ボスそうだけど、返せたらどんでん返し的なアクションポイントにしたいから
//     通常の攻撃をパリィしたときよりも体幹の蓄積を大きくして。ただし、これを弾いたからクリアしてしまうようなレベルにはしないで。」
//   作り = 普段の弾き1回の量 (連続の段は数えない・幕の体力の倍率と最後の幕の倍率と個性の倍率は普段と同じに掛ける) の gainMul 倍。
//     ただし弾いた後の体幹は「あと keepParriesToBreak 回の普段の弾きで崩れる所」までで止める (= 1回弾いただけでは崩れない)。
//     もともとそれより高かった時は下げない (今の値のまま)。
//   決めた元 = docs/research/2026-10-12-finisher-parry-posture-and-cut-polish.md の表 (各ボス・各幕の体幹の最大と普段の弾き1回の量)。
//   旧 (普段の弾き1回と同じ量・満タンなら崩れる) は DEV ?execParryGain=old だけ (URL を読むのは game.ts の DEV の箱の中)。
// ===================================================================================
export const EXEC_PARRY_POSTURE = {
  /** 普段の弾き1回の何倍か。 */
  gainMul: 3,
  /** 弾いた後、崩れるまでに残す普段の弾きの回数 (1 = 崩れる1歩手前まで)。 */
  keepParriesToBreak: 1,
} as const

let bigGain = true
/** URL の値から切り替える。'old' / '0' / 'off' = 旧 (普段の弾き1回と同じ量)。それ以外 (指定なし) は既定 (大きい量・崩れる1歩手前まで)。 */
export function setExecParryBigGain(value: string | null | undefined): boolean {
  bigGain = !(value === 'old' || value === '0' || value === 'off')
  return bigGain
}
export function execParryBigGainOn(): boolean { return bigGain }

/** 既定の作りで、致命を弾いた後のボスの体幹。normalGain = 普段の弾き1回の量 (連続の段を除く・倍率込み)。 */
export function execParryBossPostureAfter(current: number, maxPosture: number, normalGain: number): number {
  const P = EXEC_PARRY_POSTURE
  const ceiling = Math.max(0, maxPosture - normalGain * P.keepParriesToBreak)
  return Math.max(current, Math.min(current + normalGain * P.gainMul, ceiling))
}

/** 弾かれた致命の後、ボス側で変えること (今は何も変えない・のけぞりは描画の recoil が出す)。 */
export function isBossFinisherAttack(boss: Boss): boolean {
  return !!(boss.currentAttack?.bossFinisher || boss.currentAttack?.playerLethal)
}

// ===================================================================================
// 押し戻しの版 (10-09 夜・DEV ?execPush=old|new|sekiro・既定 new = 配布版・致命を弾いた時だけ)。
//   new の上に重ねる重さ (カメラの寄り・フラッシュ・揺れ・止め) は src/exec_push_weight.ts (既定 c = ボスの重さで段・10-10 ユーザー答え push-weight)。
//   ユーザー (10-09 検収パック): 弾けた時の手触り「軽い」(5体とも)・押される距離「もっと押されたい」。
//     「重たい攻撃を弾いた時に後ろに吹き飛ばされるのを耐えながら後退りするんだよね」
//   SEKIRO の動画の解析 (docs/research/2026-10-09-sekiro-heavy-parry-analysis.md「このゲームへの当てはめ」):
//     止めはほぼ 0・押される距離は身長の 0.1〜0.25 倍・光は長く残る (最大まで約180ms・半分まで約550ms・消えるまで約700ms)。
//     ユーザーの記憶と食い違うので、好みはユーザーが3つを見比べて決める:
//   old    = 前の版 (上の EXEC_PARRY と PUSH_PX のまま・1コマごとに残りの 17%・止め 210ms)。
//   new    = 解析の案B「ユーザーの記憶寄り」: 遠くへ・最初の約100msで約65%・残りを約0.4秒かけて踏ん張って止める・
//            止めは短く・重い受けの姿 (parry_heavy)・滑っている間ずっと足元の土煙。
//   sekiro = 解析の案A「SEKIRO 準拠」: 少しだけ押される・止めはほぼ無し・立ったまま (普段の parry の姿)・足元は火の粉だけ。
//   new と sekiro は、弾いた光の長い残り (exec_parry_glow) を足す。
//   ⚠️ 数値はここに集めてある。SEKIRO の解析やユーザーの答えで差し替える時はここだけ直す
//      (姿勢の数値だけは scripts/author_parry_heavy_from_parry.mjs の頭)。
// ===================================================================================

export type ExecPushVersion = 'old' | 'new' | 'sekiro'
let pushVersion: ExecPushVersion = 'new'
/** URL の値から切り替える。'old' / 'sekiro' 以外は全部 'new' (既定)。 */
export function setExecPushVersion(value: string | null | undefined): ExecPushVersion {
  pushVersion = value === 'old' || value === 'sekiro' ? value : 'new'
  return pushVersion
}
export function execPushVersion(): ExecPushVersion { return pushVersion }

export type ExecPushSpec = {
  /** 弾いた瞬間の止め (ms)。止めの間は滑らない (滑りは止め明けから)。 */
  hitStopMs: number
  /** 最初に強く押される区間の長さ (ms) と、その間に進む割合 (0..1)。 */
  firstPhaseMs: number
  firstPhaseShare: number
  /** 後半 (踏ん張って止まる) の減り方。速さ ∝ (1 − 後半の進み)^この値。大きいほど止まり際が粘る。 */
  settleExponent: number
  /** 滑り終わってから受けた姿を保つ時間 (ms・歩きの判定の持ち越し 0.15 秒より長く)。 */
  holdAfterMs: number
  /** 押されている間の姿 (Spine のアニメ名)。'parry' = 普段の弾きの姿のまま。 */
  pose: 'parry_heavy' | 'parry'
  /** 止め明けの、押される向きへの画面の揺れ (描くだけ)。振幅 0 = 揺らさない。弾いた瞬間の乱れた揺れは出さない。 */
  kick: { amplitudePx: number; durationMs: number }
  /** 足元: 'dust' = 滑っている間ずっと土煙と擦り跡 / 'embers' = 火の粉だけ (光の間)。 */
  feet: 'dust' | 'embers'
  /** 弾いた瞬間の足元の砂の量 (0 = 出さない)。 */
  impactDustAmount: number
}

export const EXEC_PUSH: Record<'new' | 'sekiro', ExecPushSpec> = {
  new: {
    hitStopMs: 70, firstPhaseMs: 100, firstPhaseShare: 0.65, settleExponent: 1.4, holdAfterMs: 170,
    pose: 'parry_heavy', kick: { amplitudePx: 5, durationMs: 180 }, feet: 'dust', impactDustAmount: 0.8,
  },
  sekiro: {
    hitStopMs: 20, firstPhaseMs: 80, firstPhaseShare: 0.7, settleExponent: 1, holdAfterMs: 170,
    pose: 'parry', kick: { amplitudePx: 0, durationMs: 0 }, feet: 'embers', impactDustAmount: 0,
  },
}

/** ボスごとの押される距離 (px) と時間 (ms)。重いボスほど遠く・長く。 */
export const EXEC_PUSH_BY_BOSS: Record<'new' | 'sekiro', Record<string, { px: number; ms: number }>> = {
  new: {
    bride: { px: 130, ms: 450 },
    'vigil-knight': { px: 140, ms: 500 },
    'dream-hound': { px: 145, ms: 500 },
    'the-executioner': { px: 150, ms: 520 },
    'crowned-king-awoken': { px: 160, ms: 550 },
  },
  sekiro: {
    bride: { px: 30, ms: 150 },
    'vigil-knight': { px: 38, ms: 200 },
    'dream-hound': { px: 40, ms: 200 },
    'the-executioner': { px: 45, ms: 220 },
    'crowned-king-awoken': { px: 50, ms: 250 },
  },
}
export const EXEC_PUSH_DEFAULT: Record<'new' | 'sekiro', { px: number; ms: number }> = {
  new: { px: 140, ms: 500 },
  sekiro: { px: 40, ms: 200 },
}

/** 足元の土煙 (new)。滑った距離 everyPx ごと、または everyMs ごとに出す。滑り終わりの tailMs 後まで出し続ける。
 *  量は今の速さ (最初の速さとの比) で minAmount〜maxAmount。足の位置 = 主人公の中心から (ゲーム px・受けの姿の足)。 */
export const EXEC_PUSH_DUST = {
  everyPx: 16, everyMs: 50, tailMs: 100, minAmount: 0.22, maxAmount: 0.75,
  backFootPx: 30, frontFootPx: 9,
} as const

/** 弾いた光の長い残り (new と sekiro・SEKIRO の動画の測定: エマ 29.2 秒の場面)。
 *  出た瞬間に最大の約6割・最大まで約180ms・半分まで約550ms・消えるまで約700ms。
 *  火の粉 (embers 粒) も光の間だけ漂う (sekiro の足元の代わりも兼ねる)。 */
export const EXEC_PARRY_GLOW = {
  startShare: 0.6, peakMs: 180, halfMs: 550, endMs: 700,
  radiusPx: 46, embers: 10,
} as const

export type ExecPushPlan = {
  /** 押される向き (+1 = 右へ)。 */
  dir: 1 | -1
  /** 押される距離 (px・壁ぎわでは壁までの残りで縮めた後の値)。 */
  px: number
  durationMs: number
  /** 止め明けからの経過 (ms・player.ts が1コマごとに進める)。 */
  elapsedMs: number
  /** これまでに進んだ距離 (px)。 */
  traveledPx: number
  /** 弾いた時のパリィの押した時刻 (描画: もう一度押して新しいパリィになったら重い受けの姿をやめる)。 */
  pressMs: number
  version: 'new' | 'sekiro'
}

export function execPushSpec(version: 'new' | 'sekiro' = pushVersion === 'sekiro' ? 'sekiro' : 'new'): ExecPushSpec {
  return EXEC_PUSH[version]
}
export function execPushFor(bossId: string, version: 'new' | 'sekiro'): { px: number; ms: number } {
  return EXEC_PUSH_BY_BOSS[version][bossId] ?? EXEC_PUSH_DEFAULT[version]
}

/** 押し戻しの進み方 (u = 経過/全体の時間 0..1 → 進んだ割合 0..1)。2段:
 *  ① 最初の firstPhaseMs: 強い初速から速さが直線で落ちながら firstPhaseShare まで進む。
 *  ② 残り: 速さ ∝ (1−s)^settleExponent で踏ん張って止まる。①の終わりと②の始まりの速さはそろえてある (段がつかない)。 */
export function execPushProgress(u: number, durationMs: number, spec: ExecPushSpec): number {
  if (u <= 0) return 0
  if (u >= 1) return 1
  const u1 = Math.min(0.9, Math.max(0.05, spec.firstPhaseMs / durationMs))
  const a = spec.firstPhaseShare
  const q = spec.settleExponent
  const v1 = (1 - a) * (q + 1) / (1 - u1)          // ②の頭の速さ (割合/u)
  const v0 = Math.max(v1, 2 * a / u1 - v1)          // ①の頭の速さ
  if (u <= u1) return Math.min(a, v0 * u - (v0 - v1) * u * u / (2 * u1))
  const s = (u - u1) / (1 - u1)
  return a + v1 * (1 - u1) / (q + 1) * (1 - (1 - s) ** (q + 1))
}

/** 押し戻しの計画を立てる (弾いた瞬間・hits.ts)。壁ぎわでは壁までの残りで縮める (めり込まない・壁で急に止まらない)。 */
export function planExecPush(bossId: string, playerX: number, dir: 1 | -1, bounds: { leftWall: number; rightWall: number },
  pressMs: number, version: 'new' | 'sekiro'): ExecPushPlan {
  const base = execPushFor(bossId, version)
  const room = Math.max(0, dir > 0 ? bounds.rightWall - playerX : playerX - bounds.leftWall)
  return { dir, px: Math.min(base.px, room), durationMs: base.ms, elapsedMs: 0, traveledPx: 0, pressMs, version }
}

/** 押し戻しを1コマ進める (player.ts)。戻り値の dx だけ主人公を動かす。滑り終わった後も経過だけは進める (描画の姿の時計)。 */
export function stepExecPush(plan: ExecPushPlan, dtMs: number): { plan: ExecPushPlan; dx: number } {
  const elapsedMs = plan.elapsedMs + dtMs
  const target = plan.px * execPushProgress(elapsedMs / plan.durationMs, plan.durationMs, EXEC_PUSH[plan.version])
  return { plan: { ...plan, elapsedMs, traveledPx: target }, dx: plan.dir * (target - plan.traveledPx) }
}

/** 滑っている途中か。 */
export function execPushSliding(plan: ExecPushPlan | undefined): boolean {
  return !!plan && plan.elapsedMs < plan.durationMs && plan.px > 0
}

/** 今の速さの、最初の速さとの比 (0..1・足元の土煙の量に使う)。 */
export function execPushSpeedRatio(plan: ExecPushPlan): number {
  const spec = EXEC_PUSH[plan.version]
  const d = plan.durationMs
  const v = (u: number) => execPushProgress(Math.min(1, u + 1 / d), d, spec) - execPushProgress(u, d, spec)
  const v0 = v(0)
  return v0 > 0 ? Math.max(0, Math.min(1, v(plan.elapsedMs / d) / v0)) : 0
}

/** 足元の土煙 (new だけ・game.ts から毎コマ)。prev / next = このコマの前後の主人公。 */
export function execPushDustFx(prev: Player, next: Player, now: number): FxEvent[] {
  const a = prev.execPush, b = next.execPush
  if (!a || !b || a.pressMs !== b.pressMs || b.px <= 0 || EXEC_PUSH[b.version].feet !== 'dust') return []
  const D = EXEC_PUSH_DUST
  if (a.elapsedMs >= b.durationMs + D.tailMs) return []
  const crossedPx = Math.floor(b.traveledPx / D.everyPx) > Math.floor(a.traveledPx / D.everyPx)
  const crossedMs = Math.floor(b.elapsedMs / D.everyMs) > Math.floor(a.elapsedMs / D.everyMs)
  if (!crossedPx && !crossedMs) return []
  const amount = D.minAmount + (D.maxAmount - D.minAmount) * execPushSpeedRatio(b)
  const dir = b.dir
  const out: FxEvent[] = []
  // 後ろ足: 滑った所に床の擦り跡と低い土煙 (押される向きへ流れる)。止まった後は足元だけ小さく。
  const x1 = next.x + dir * D.backFootPx
  const x0 = Math.abs(next.x - prev.x) > 0.5 ? prev.x + dir * D.backFootPx : x1 - dir * 4
  out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: x0, x2: x1, y: next.y, t: now, dir: -dir, amount })
  // 前足: 体重を残した前足が床を押して、押される向きへ土煙が舞う (滑っている間だけ・距離ごと)。
  if (crossedPx && execPushSliding(b)) {
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x - dir * D.frontFootPx, y: next.y, t: now, dir: -dir, amount: amount * 0.8 })
  }
  return out
}

/** 止め明けの、押される向きへの画面の揺れ (描くだけ・render.ts)。{x, y} px。 */
export function execPushCameraKick(player: Player, playing: boolean): { x: number; y: number } {
  const plan = player.execPush
  if (!playing || !plan) return { x: 0, y: 0 }
  const k = EXEC_PUSH[plan.version].kick
  if (k.amplitudePx <= 0 || plan.elapsedMs >= k.durationMs) return { x: 0, y: 0 }
  const t = plan.elapsedMs / k.durationMs
  const amp = k.amplitudePx * Math.sin(Math.PI * Math.min(1, t * 2.2)) * (1 - t) ** 1.5
  return { x: plan.dir * amp, y: amp * 0.25 }
}

/** 描画: 押されている間の姿。重い受けの姿 (parry_heavy) を出すなら、その経過 (秒)。出さないなら undefined。 */
export function execHeavyPoseSec(player: Player): number | undefined {
  const plan = player.execPush
  if (!plan || player.state !== 'parrying' || EXEC_PUSH[plan.version].pose !== 'parry_heavy') return undefined
  if (player.lastParryPressMs !== plan.pressMs) return undefined
  return plan.elapsedMs / 1000
}

/** 光の長い残りの明るさ (0..1・age 秒)。出た瞬間に startShare・peakMs で最大・halfMs で半分・endMs で 0。 */
export function execParryGlowLevel(ageSec: number): number {
  const G = EXEC_PARRY_GLOW
  const ms = ageSec * 1000
  if (ms < 0 || ms >= G.endMs) return 0
  if (ms <= G.peakMs) return G.startShare + (1 - G.startShare) * Math.sin((ms / G.peakMs) * Math.PI / 2)
  if (ms <= G.halfMs) {
    const k = (ms - G.peakMs) / (G.halfMs - G.peakMs)
    return 1 - 0.5 * k * k * (3 - 2 * k)
  }
  const k = (ms - G.halfMs) / (G.endMs - G.halfMs)
  return 0.5 * (1 - k) * (1 - k)
}
